import { TTranslator } from '@smartshell/ui-kit';
import day from 'dayjs';
import { RowInput } from 'jspdf-autotable';

import { TLocalesKeys } from 'localization';
import { PDFGenerator } from 'services/PDFGenerator';
import { IWorker } from 'types';

import { IGeneratePDFArgs } from './interfaces';

export const formatDate = (date?: string): string =>
  (date ? day(date) : day()).format('D MMM YYYY HH:mm');

export const getFormattedDuration = (
  t: TTranslator<TLocalesKeys>,
  start: string,
  finish?: string
): string => {
  const startDate = day(start);
  const finishDate = finish ? day(finish) : day();

  const hours = finishDate.diff(startDate, 'hours');
  const minutes = finishDate.add(-hours, 'hours').diff(startDate, 'minutes');

  return `${hours} ${t('time.contracted.hours')} ${minutes} ${t(
    'time.contracted.minutes'
  )}`;
};

export const formatFIO = ({
  last_name,
  first_name,
}: Partial<IWorker>): string =>
  `${last_name} ${first_name && first_name[0] + '.'}`;

export const formatNumber = (num: number): string =>
  num.toLocaleString(undefined, { maximumFractionDigits: 2 });

export const formatMoney = (money: number, currency: string): string =>
  `${formatNumber(money)} ${currency}`;
export const getPercent = (money: number, total: number): string =>
  `${formatNumber((money * 100) / total)}%`;

export const generatePDF = async ({
  t,
  services,
  goods,
  combos,
  cashOrders,
  currency,
  comment,
  useFooter,
  printReportAllGoods,
  allGoods,
}: IGeneratePDFArgs): Promise<URL> => {
  const header = document.getElementById('report-header');
  const footer = useFooter && document.getElementById('report-footer');

  if (!header) {
    throw new Error('report components not exist');
  }
  const generator = new PDFGenerator(footer || undefined);

  await generator.insertHTML(header);

  generator.insertText({
    text: t(
      comment ? 'comment' : 'reports.workShift.print.comment.placeholder'
    ),
    isTitle: true,
  });

  if (comment) {
    generator.insertText({
      text: comment,
    });
  }

  const orderRows: Array<RowInput> = [];
  const orderColumns = [
    { dataKey: 'sum', header: t('sum') },
    t('comment'),
    { dataKey: 'date', header: t('reports.workShift.print.orders.time') },
  ];

  cashOrders.forEach((order) => {
    const ticketData = [
      `${
        order.type === 'PKO' ? '+' : '-'
      }${order.sum.toLocaleString()} ${currency}`,
      order.comment,
      day(order.created_at).format('HH:mm'),
    ];
    orderRows.push(ticketData);
  });

  generator.insertText({
    text: t(
      cashOrders.length
        ? 'reports.workShift.print.orders'
        : 'reports.workShift.print.orders.placeholder'
    ),
    isTitle: true,
  });
  await generator.insertTable({
    columns: orderColumns,
    body: orderRows,
    columnStyles: {
      sum: {
        cellWidth: 100,
      },
      date: {
        cellWidth: 80,
      },
    },
  });

  /** Генерация таблицы с сервисами */

  const servicesColumns = [
    t('title'),
    { dataKey: 'amount', header: t('amount') },
    { dataKey: 'total', header: t('total') },
  ];
  const servicesRows: Array<RowInput> = [];
  let servicesTotal = 0;

  services?.forEach((service) => {
    servicesTotal += service.sum;

    const data = [
      service.title,
      `${service.amount.toLocaleString()} ${t('pcs')}`,
      `${service.sum.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })} ${currency}`,
    ];
    servicesRows.push(data);
  });

  const servicesTitle = services?.length
    ? t(
        'reports.workShift.print.services',
        servicesTotal.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        }),
        currency
      )
    : t('reports.workShift.print.services.placeholder');

  generator.insertText({
    text: servicesTitle,
    isTitle: true,
  });
  await generator.insertTable({
    columns: servicesColumns,
    body: servicesRows,
    columnStyles: {
      amount: {
        cellWidth: 100,
      },
      total: {
        cellWidth: 80,
      },
    },
  });

  /** Генерация таблицы с комбо-наборами */

  const combosColumns = [
    t('title'),
    { dataKey: 'amount', header: t('amount') },
    { dataKey: 'total', header: t('total') },
  ];
  const combosRows: Array<RowInput> = [];
  let combosTotal = 0;

  combos?.forEach((combo) => {
    combosTotal += combo.sum;

    const data = [
      combo.title,
      `${combo.amount.toLocaleString()} ${t('pcs')}`,
      `${combo.sum.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })} ${currency}`,
    ];
    combosRows.push(data);
  });

  const combosTitle = combos?.length
    ? t(
        'reports.workShift.print.combo',
        combosTotal.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        }),
        currency
      )
    : t('reports.workShift.print.combo.placeholder');

  generator.insertText({
    text: combosTitle,
    isTitle: true,
  });
  await generator.insertTable({
    columns: combosColumns,
    body: combosRows,
    columnStyles: {
      amount: {
        cellWidth: 100,
      },
      total: {
        cellWidth: 80,
      },
    },
  });

  /** Генерация таблицы с товарами */

  const goodsColumns = [
    { dataKey: 'title', header: t('title') },
    t('reports.workShift.print.price'),
    t('reports.workShift.print.sold'),
    t('reports.workShift.print.rest'),
    t('reports.workShift.print.fact'),
    { dataKey: 'total', header: t('total') },
  ];
  const goodsRows: Array<RowInput> = [];
  let goodsTotal = 0;

  goods?.forEach((good) => {
    goodsTotal += good.sum;

    const data = [
      good.title,
      `${good.cost.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })} ${currency}`,
      `${good.amount.toLocaleString()} ${t('pcs')}`,
      `${good.in_stock.toLocaleString()} ${t('pcs')}`,
      '________',
      `${good.sum.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })} ${currency}`,
    ];
    goodsRows.push(data);
  });

  generator.addPage();

  const goodsTitle = goods?.length
    ? t(
        'reports.workShift.print.goods',
        goodsTotal.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        }),
        currency
      )
    : t('reports.workShift.print.goods.placeholder');

  generator.insertText({ text: goodsTitle, isTitle: true });
  await generator.insertTable({
    columns: goodsColumns,
    body: goodsRows,
    columnStyles: {
      title: {
        cellWidth: 300,
      },
      total: {
        cellWidth: 80,
      },
    },
  });

  /** Генерация таблицы со всеми товарами */

  const goodsAllColumns = [
    { dataKey: 'title', header: t('title') },
    t('reports.workShift.print.fact'),
    t('reports.workShift.print.rest'),
  ];
  const goodsAllRows: Array<RowInput> = [];

  allGoods?.forEach((good) => {
    const data = [
      good.title,
      '________',
      `${good.amount.toLocaleString()} ${t('pcs')}`,
    ];
    goodsAllRows.push(data);
  });

  const goodsAllTitle = t('reports.workShift.print.goods_all');

  if (printReportAllGoods) {
    generator.insertText({ text: goodsAllTitle, isTitle: true });
    await generator.insertTable({
      columns: goodsAllColumns,
      body: goodsAllRows,
      columnStyles: {
        title: {
          cellWidth: 580,
        },
      },
    });
  }

  return generator.getPDFUrl();
};
